import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import './App.css';
import './styles/nestedDialog.css';
import LandingPage from './Components/LandingPage/landingPage';
import SearchSalonPage from './Components/LandingPage/sections/searchSalon';
import SalonDetails from './Components/LandingPage/sections/SalonDetails';
import ClientView from './Components/client/clientView';
import DashboardLayout from './Components/pro/dashboard.jsx';
import ProtectedRoute from './Components/ProtectedRoute';
import RequestPasswordReset from './Components/Auth/RequestPasswordReset';
import ResetPassword from './Components/Auth/ResetPassword';
import ClientServices from './Components/client/salonDetails/ClientServices';
import SuperAdminApp from './Components/superadmin/SuperAdminApp';
import LoginPage from './Components/Auth/LoginPage';
import Terms from './Components/Legal/Terms';
import Privacy from './Components/Legal/Privacy';
import Presentation from './Components/Legal/Presentation';
import DeleteAccount from './Components/Legal/DeleteAccount';
import About from './Components/Info/About';
import Team from './Components/Info/Team';
import Contact from './Components/Info/Contact';
import Career from './Components/Info/Career';
import SalonPublicThemeRoute from './Components/common/SalonPublicThemeRoute';
import {
  ClientChatProvider,
} from './Components/client/messaging/widget/ClientChatContext';
import ClientChatWidget from './Components/client/messaging/widget/ClientChatWidget';

const ltrTheme = createTheme({ direction: 'ltr' });
const rtlTheme = createTheme({ direction: 'rtl' });
const ltrCache = createCache({ key: 'muiltr' });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [rtlPlugin] });

function App() {
  const { i18n } = useTranslation();
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  const muiTheme = direction === 'rtl' ? rtlTheme : ltrTheme;
  const cache = direction === 'rtl' ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <Router>
          <ClientChatProvider>
            <div className="App">
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/search-salon" element={<SearchSalonPage />} />
              <Route
                path="/salon/:salonId"
                element={(
                  <SalonPublicThemeRoute>
                    <SalonDetails />
                  </SalonPublicThemeRoute>
                )}
              />
              <Route
                path="/salon/:salonId/services"
                element={(
                  <SalonPublicThemeRoute>
                    <ClientServices />
                  </SalonPublicThemeRoute>
                )}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/request-password-reset" element={<RequestPasswordReset />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/legal/conditions" element={<Terms />} />
              <Route path="/legal/confidentialite" element={<Privacy />} />
              <Route path="/legal/presentation" element={<Presentation />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/equipe" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/carriere" element={<Career />} />
              
              {/* Protected routes */}
              <Route path="/client/*" element={<ProtectedRoute><ClientView /></ProtectedRoute>} />
              <Route path="/pro/*" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>} />
              <Route
                path="/superadmin/*"
                element={
                  <ProtectedRoute requiredAction="manage" requiredSubject="all" fallbackRoute="/">
                    <SuperAdminApp />
                  </ProtectedRoute>
                }
              />
              
              {/* Redirect unknown routes to home */}
              <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              {/* Dialog container for accessibility - prevents aria-hidden issues */}
              <div id="dialog-container" />
            </div>
            <ClientChatWidget />
          </ClientChatProvider>
        </Router>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;

